/**
 * AntragSection - the Tab-5a «Antrag» body (P2 Slice 13). Renders the Kundenentscheid card, the
 * prepared Antrag (document / signature / Einreichung) and the action buttons; hosts the six dialogs.
 * The server owns every gate; the buttons are driven by `aktionen` + `canWrite`.
 */

import { useEffect, useState } from 'react';
import {
  ANTRAG_BOUNCE_HINWEIS,
  ANTRAG_FRIST_ABGELAUFEN_HINWEIS,
  ANTRAG_KEIN_ENTSCHEID_HINWEIS,
} from '@brokly/shared';
import { ApiError } from '../../lib/api-client';
import { dateShortDe, dateTimeShortDe } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { AntragStatusBadge } from './AntragStatusBadge';
import { AntragVorbereitenDialog } from './AntragVorbereitenDialog';
import { AntragSignaturDialog } from './AntragSignaturDialog';
import { AntragSigniertUploadDialog } from './AntragSigniertUploadDialog';
import { AntragEinreichenDialog } from './AntragEinreichenDialog';
import { AntragVerwerfenDialog } from './AntragVerwerfenDialog';
import { AntragFristDialog } from './AntragFristDialog';
import {
  usePlacementAntrag,
  useVorbereitenAntrag,
  useAktualisierenAntrag,
  useSignaturAntrag,
  useErneutSignaturAntrag,
  useFristAntrag,
  useAbfragenSignaturAntrag,
  useSigniertHochladenAntrag,
  useEinreichenAntrag,
  useVersandAufloesenAntrag,
  useVerwerfenAntrag,
} from './usePlacementAntrag';

type DialogKind = 'vorbereiten' | 'bearbeiten' | 'signatur' | 'signiert' | 'einreichen' | 'verwerfen' | 'frist' | null;

function errBody(err: unknown): { code?: string } | null {
  if (err instanceof ApiError) return { code: err.code };
  if (err instanceof Error) return { code: err.message };
  return err ? { code: String(err) } : null;
}

export function AntragSection({
  placementId,
  canWrite,
  openKind,
  onOpenHandled,
}: {
  placementId: string;
  canWrite: boolean;
  openKind?: 'vorbereiten' | 'signatur' | 'einreichen' | null;
  onOpenHandled?: () => void;
}) {
  const query = usePlacementAntrag(placementId);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [error, setError] = useState<{ code?: string } | null>(null);

  const vorbereiten = useVorbereitenAntrag(placementId);
  const aktualisieren = useAktualisierenAntrag(placementId);
  const signatur = useSignaturAntrag(placementId);
  const erneut = useErneutSignaturAntrag(placementId);
  const frist = useFristAntrag(placementId);
  const abfragen = useAbfragenSignaturAntrag(placementId);
  const signiert = useSigniertHochladenAntrag(placementId);
  const einreichen = useEinreichenAntrag(placementId);
  const versandAufloesen = useVersandAufloesenAntrag(placementId);
  const verwerfen = useVerwerfenAntrag(placementId);

  useEffect(() => {
    if (openKind) {
      setDialog(openKind);
      onOpenHandled?.();
    }
  }, [openKind, onOpenHandled]);

  if (query.isLoading || !query.data) {
    return <p className="p-4 text-sm text-muted-foreground">Lade Antrag…</p>;
  }
  const facts = query.data;
  const a = facts.antrag;
  const akt = facts.aktionen;

  const close = () => {
    setDialog(null);
    setError(null);
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      close();
    } catch (err) {
      setError(errBody(err));
    }
  };

  return (
    <section className="space-y-4" data-testid="placement-antrag-section">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold">Antrag</h3>
          <AntragStatusBadge status={a?.status ?? null} ueberfaellig={a?.signatur.ueberfaellig} />
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            {facts.vorbereiten_erlaubt && (
              <Button onClick={() => setDialog('vorbereiten')} data-testid="antrag-action-vorbereiten">Antrag vorbereiten</Button>
            )}
            {akt.bearbeiten && <Button variant="ghost" onClick={() => setDialog('bearbeiten')} data-testid="antrag-action-bearbeiten">Bearbeiten</Button>}
            {akt.signatur_senden && <Button onClick={() => setDialog('signatur')} data-testid="antrag-action-signatur">Zur Unterschrift senden</Button>}
            {akt.erneut_senden && <Button variant="ghost" onClick={() => void run(() => erneut.mutateAsync())} data-testid="antrag-action-erneut">Erneut zustellen</Button>}
            {akt.frist_aendern && <Button variant="ghost" onClick={() => setDialog('frist')} data-testid="antrag-action-frist">Frist anpassen</Button>}
            {akt.status_abfragen && <Button variant="ghost" onClick={() => void run(() => abfragen.mutateAsync())} data-testid="antrag-action-abfragen">Signaturstatus abfragen</Button>}
            {akt.signiert_hochladen && <Button variant="ghost" onClick={() => setDialog('signiert')} data-testid="antrag-action-signiert">Unterschriebenen Antrag hochladen</Button>}
            {akt.einreichen && <Button onClick={() => setDialog('einreichen')} data-testid="antrag-action-einreichen">Einreichen</Button>}
            {akt.verwerfen && <Button variant="ghost" onClick={() => setDialog('verwerfen')} data-testid="antrag-action-verwerfen">Verwerfen</Button>}
          </div>
        )}
      </header>

      {!facts.vorbereiten_erlaubt && !a && (
        <div className="rounded-md border border-border p-3 text-sm text-muted-foreground" data-testid="antrag-hint">
          {facts.sperrgrund === 'kein_kundenentscheid' ? (
            <span>
              {ANTRAG_KEIN_ENTSCHEID_HINWEIS}{' '}
              <button type="button" className="underline" data-testid="antrag-zu-4c">Zu Schritt 4c</button>
            </span>
          ) : facts.sperrgrund === 'placement_locked' ? (
            'Ausschreibung abgeschlossen - nur lesend'
          ) : (
            'Antrag erst nach dem Kundenentscheid moeglich'
          )}
        </div>
      )}

      {facts.aktion_sperrgrund === 'placement_status_invalid' && (
        <div className="rounded-md border border-status-warning/40 bg-status-warning/10 p-3 text-sm text-status-warning" data-testid="antrag-aktion-sperre">
          Ausschreibung abgebrochen - keine Antragsaktionen mehr moeglich.
        </div>
      )}

      {facts.entscheid && (
        <div className="rounded-md border border-border p-3 text-sm" data-testid="antrag-entscheid-card">
          Kundenentscheid: {facts.entscheid.carrier_name ?? facts.entscheid.carrier_uid} ({facts.entscheid.submission_number}, V{facts.entscheid.version_no}) am{' '}
          {dateShortDe(facts.entscheid.decided_at)}
          {facts.entscheid.entscheider_name ? ` - ${facts.entscheid.entscheider_name}` : ''}
          {facts.entscheid.praemie_jahr != null ? ` - CHF ${facts.entscheid.praemie_jahr}` : ''}
          {facts.entscheid.versicherungsbeginn ? `, Beginn ${dateShortDe(facts.entscheid.versicherungsbeginn)}` : ''}
        </div>
      )}

      {a && (
        <div className="space-y-3">
          <div className="rounded-md border border-border p-3 text-sm" data-testid="antrag-dokument-card">
            <div className="flex items-center gap-2">
              <span className="font-medium">Version {a.version_no}</span>
              <span className="text-xs text-muted-foreground">{a.form_quelle_label}{a.form_template_name ? ` - ${a.form_template_name}` : ''}</span>
              <span className="text-xs text-muted-foreground">({a.doc.engine})</span>
            </div>
            <div className="text-xs text-muted-foreground">erstellt am {dateTimeShortDe(a.doc.rendered_at)}{a.signatur.versendet_by_name ? ` von ${a.signatur.versendet_by_name}` : ''}</div>
            {a.doc.hidden_reason === 'permission' ? (
              <div data-testid="antrag-doc-hidden">
                <span className="text-sm">{a.doc.filename}</span>
                <p className="text-xs text-muted-foreground">Kein Zugriff auf das Dokument (fehlende Berechtigung).</p>
              </div>
            ) : (
              <a href={a.doc.datei_url} className="text-sm underline" data-testid="antrag-doc-download">{a.doc.filename}</a>
            )}
            {a.prefill_hinweise.map((h, i) => (
              <p key={i} role="note" className="text-xs text-muted-foreground">{h}</p>
            ))}
          </div>

          {a.signatur.channel && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="antrag-signatur-card">
              <div>{a.signatur.channel_label}{a.signatur.versendet_at ? ` - versendet am ${dateTimeShortDe(a.signatur.versendet_at)}` : ''}</div>
              <div className="text-xs text-muted-foreground">{a.signatur.versand_count}x zugestellt{a.signatur.frist_date ? ` - Frist ${dateShortDe(a.signatur.frist_date)}` : ''}</div>
              {a.signatur.ueberfaellig && <p className="text-sm text-status-danger" data-testid="antrag-ueberfaellig">{ANTRAG_FRIST_ABGELAUFEN_HINWEIS}</p>}
              {a.signatur.eskaliert_at && <p className="text-xs text-muted-foreground">eskaliert am {dateTimeShortDe(a.signatur.eskaliert_at)}</p>}
              {a.signatur.esign && (
                <p className="text-xs text-muted-foreground">
                  {a.signatur.esign.tier} - {a.signatur.esign.provider} - {a.signatur.esign.status}
                </p>
              )}
              {a.versand_laeuft && <p className="text-xs text-muted-foreground">Versand laeuft…</p>}
              {a.versand_gesperrt && (
                <div className="space-y-1" data-testid="antrag-versand-gesperrt">
                  <p className="text-sm text-status-danger">E-Mail gesendet, aber nicht erfasst. Bitte auflösen.</p>
                  <div className="flex gap-2">
                    {a.nachtragen_moeglich && (
                      <Button size="sm" variant="secondary" disabled={!canWrite || versandAufloesen.isPending} onClick={() => versandAufloesen.mutate('nachtragen')} data-testid="antrag-nachtragen">
                        Als versandt erfassen
                      </Button>
                    )}
                    <Button size="sm" variant="secondary" disabled={!canWrite || versandAufloesen.isPending} onClick={() => versandAufloesen.mutate('freigeben')} data-testid="antrag-freigeben">
                      Erneuten Versand freigeben
                    </Button>
                  </div>
                </div>
              )}
              {a.letzter_versandfehler && !a.versand_gesperrt && <p className="text-sm text-status-danger" data-testid="antrag-versandfehler">{a.letzter_versandfehler}</p>}
              {a.signiert && (
                <p className="text-xs text-muted-foreground">
                  eingegangen am {dateTimeShortDe(a.signiert.received_at)} via {a.signiert.quelle_label}
                  {a.signiert.erfasst_by_name ? `, erfasst von ${a.signiert.erfasst_by_name}` : ''} -{' '}
                  {a.signiert.hidden_reason === 'permission' ? (
                    <span data-testid="antrag-signiert-hidden">{a.signiert.filename} (kein Zugriff, fehlende Berechtigung)</span>
                  ) : (
                    <a href={a.signiert.datei_url} className="underline" data-testid="antrag-signiert-download">{a.signiert.filename}</a>
                  )}
                </p>
              )}
            </div>
          )}

          {(a.einreichung || a.carrier_reply) && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="antrag-einreichung-card">
              {a.einreichung && (
                <div>
                  eingereicht am {dateTimeShortDe(a.einreichung.submitted_at)}{a.einreichung.submitted_by_name ? ` von ${a.einreichung.submitted_by_name}` : ''} an {a.einreichung.empfaenger}
                  {a.einreichung.bounce_at && <p className="text-sm text-status-danger" data-testid="antrag-bounce">{ANTRAG_BOUNCE_HINWEIS}</p>}
                </div>
              )}
              {a.carrier_reply && (
                <div className="text-xs text-muted-foreground">
                  Rueckmeldung des Versicherers eingegangen am {dateTimeShortDe(a.carrier_reply.received_at)} -{' '}
                  <a href="#underwriting" className="underline" data-testid="antrag-zum-underwriting">Zum Underwriting</a>
                </div>
              )}
            </div>
          )}

          {a.emails.length > 0 && (
            <ul className="space-y-1 text-xs text-muted-foreground" data-testid="antrag-emails">
              {a.emails.map((e) => (
                <li key={e.id}>
                  {e.art_label} - {e.empfaenger} - {dateTimeShortDe(e.sent_at)}{e.bounce_at ? ' (bounce)' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {facts.versionen.length > 1 && (
        <details data-testid="antrag-versionen">
          <summary className="cursor-pointer text-sm">Versionen</summary>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {facts.versionen.map((v) => (
              <li key={v.id}>
                Version {v.version_no} - {v.status_label}
                {v.verworfen ? ` - verworfen am ${dateTimeShortDe(v.verworfen.at)} (${v.verworfen.grund})` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}

      {(dialog === 'vorbereiten' || dialog === 'bearbeiten') && (
        <AntragVorbereitenDialog
          open
          onClose={close}
          facts={facts}
          mode={dialog === 'bearbeiten' ? 'bearbeiten' : 'neu'}
          saving={vorbereiten.isPending || aktualisieren.isPending}
          error={error}
          onVorbereiten={(body) => void run(() => (dialog === 'bearbeiten' ? aktualisieren.mutateAsync(body) : vorbereiten.mutateAsync(body)))}
        />
      )}
      {dialog === 'signatur' && (
        <AntragSignaturDialog open onClose={close} facts={facts} saving={signatur.isPending} error={error} onSenden={(body) => void run(() => signatur.mutateAsync(body))} />
      )}
      {dialog === 'signiert' && (
        <AntragSigniertUploadDialog open onClose={close} saving={signiert.isPending} error={error} onUpload={(file) => void run(() => signiert.mutateAsync(file))} />
      )}
      {dialog === 'einreichen' && (
        <AntragEinreichenDialog open onClose={close} facts={facts} saving={einreichen.isPending} error={error} onEinreichen={() => void run(() => einreichen.mutateAsync())} />
      )}
      {dialog === 'verwerfen' && (
        <AntragVerwerfenDialog open onClose={close} saving={verwerfen.isPending} error={error} onVerwerfen={(body) => void run(() => verwerfen.mutateAsync(body))} />
      )}
      {dialog === 'frist' && (
        <AntragFristDialog open onClose={close} currentFrist={a?.signatur.frist_date ?? null} saving={frist.isPending} error={error} onFrist={(body) => void run(() => frist.mutateAsync(body))} />
      )}
    </section>
  );
}
